import { useMemo } from "react";
import { useGetList } from "ra-core";
import { Link } from "react-router";
import { ExternalLink } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  studioFilmsListPath,
  summarizeFilmsByCreator,
} from "../echoflicks/studioFilms";
import {
  latestStudioRatings,
  ratingSummary,
} from "../echoflicks/studioRatings";
import type {
  Agency,
  AgencyPortalUser,
  EchoflicksVideoSession,
} from "../types";
import { AgentRatingsTable } from "./AgentRatingsTable";
import { INTERNAL_FLAG_EFFECT, isInternalAgency } from "./internalAgencies";

/**
 * "Films by creator" for one agency (B6.3, plan 2026-09-26 §3.3): which of its
 * logins made which films, how they ended and how many were re-cuts — "sort it
 * by company, and then … by which one of the email addresses made it".
 *
 * Reads the dashboard's SESSION list filtered by `studio_agency_id` (a failed
 * film never gets an ECHO row, and failures are the point). Creators are portal
 * user ids on the dashboard rows; their e-mails come from the logins this
 * admin-only page already loads (plan G10: no e-mails in the dashboard).
 */
export const FilmsByCreatorSection = ({
  agency,
  portalUsers,
}: {
  agency: Agency;
  /** Every portal login (a creator may have moved agency since). */
  portalUsers: readonly AgencyPortalUser[];
}) => {
  const agencyId = String(agency.id);
  const {
    data: films,
    isPending,
    error,
  } = useGetList<EchoflicksVideoSession>(
    "echoflicks_video_sessions",
    {
      filter: { studio_agency_id: agencyId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "display_date", order: "DESC" },
    },
    { staleTime: 60 * 1000, retry: 1 },
  );
  const loginsById = useMemo(
    () => new Map(portalUsers.map((user) => [String(user.id), user])),
    [portalUsers],
  );
  const rows = useMemo(
    () => summarizeFilmsByCreator(films ?? [], loginsById),
    [films, loginsById],
  );
  // Agent ratings (plan 2026-10-10): the score and words land on the same session rows.
  const ratings = useMemo(
    () => latestStudioRatings(films ?? [], loginsById),
    [films, loginsById],
  );
  const ratingTotals = useMemo(() => ratingSummary(films ?? []), [films]);
  const totals = rows.reduce(
    (sum, row) => ({
      films: sum.films + row.films,
      recuts: sum.recuts + row.recuts,
      failed: sum.failed + row.failed,
    }),
    { films: 0, recuts: 0, failed: 0 },
  );

  return (
    <section>
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Films by creator</h3>
        <Button asChild size="sm" variant="outline">
          <Link to={studioFilmsListPath("echoflicks_video_sessions", agencyId)}>
            All films
            <ExternalLink className="ml-1 h-3 w-3" />
          </Link>
        </Button>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        Every Studio film of this agency by the portal login that made it,
        re-cuts and failed films included.
        {isInternalAgency(agency)
          ? ` This studio is internal: ${INTERNAL_FLAG_EFFECT}.`
          : ""}
      </p>
      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading films…</p>
      ) : error ? (
        <p className="text-sm text-muted-foreground">
          Films could not be loaded from the dashboard. It needs the dashboard
          API with the Studio filters (B6.2); try again later.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No Studio films recorded for this agency yet. Films made before the
          dashboard recorded agencies appear once the film-facts backfill has
          run.
        </p>
      ) : (
        <>
          <p className="mb-1 text-xs text-muted-foreground">
            {totals.films} {totals.films === 1 ? "film" : "films"} ·{" "}
            {totals.recuts} {totals.recuts === 1 ? "re-cut" : "re-cuts"} ·{" "}
            {totals.failed} failed
            {ratingTotals.rated
              ? ` · ${ratingTotals.rated} rated, average ${ratingTotals.average} ★`
              : ""}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Creator</TableHead>
                <TableHead className="text-right">Films</TableHead>
                <TableHead className="text-right">Delivered</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">Running</TableHead>
                <TableHead className="text-right">Re-cuts</TableHead>
                <TableHead className="text-right">Rating</TableHead>
                <TableHead>Formats</TableHead>
                <TableHead>Last film</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id ?? "unknown"}>
                  <TableCell>
                    <span className="mr-1" title={row.id ?? undefined}>
                      {row.label}
                    </span>
                    {row.isStaff ? (
                      <Badge variant="secondary" className="text-xs">
                        Staff
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right">{row.films}</TableCell>
                  <TableCell className="text-right">{row.delivered}</TableCell>
                  <TableCell className="text-right">
                    {row.failed ? (
                      <Badge variant="destructive" className="text-xs">
                        {row.failed}
                      </Badge>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.inProgress}
                    {row.cancelled ? (
                      <span className="text-xs text-muted-foreground">
                        {" "}
                        (+{row.cancelled} cancelled)
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right">{row.recuts}</TableCell>
                  <TableCell
                    className="whitespace-nowrap text-right text-xs"
                    title={
                      row.rated
                        ? `${row.rated} ${row.rated === 1 ? "film" : "films"} rated`
                        : undefined
                    }
                  >
                    {row.averageRating != null
                      ? `${row.averageRating} ★ (${row.rated})`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.formats.length
                      ? row.formats
                          .map((format) => `${format.label} ×${format.count}`)
                          .join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.lastFilmAt
                      ? new Date(row.lastFilmAt).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {row.id ? (
                      <Link
                        to={studioFilmsListPath(
                          "echoflicks_video_sessions",
                          agencyId,
                          row.id,
                        )}
                        className="text-xs underline-offset-2 hover:underline"
                      >
                        Films
                      </Link>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <AgentRatingsTable ratings={ratings} />
        </>
      )}
    </section>
  );
};
